import { useState } from "react";
import { motion } from "framer-motion";
import { User, Mail, Calendar, Trophy, Target, Hash, Type, Flame, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import Header from "../components/Header";
import SubscriptionPanel from "../components/SubscriptionPanel";
import AchievementsCard from "../components/AchievementsCard";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { useEffect } from "react";
import { toast } from "sonner";

const Profile = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [streak, setStreak] = useState(null);
  const [privacy, setPrivacy] = useState({ show_on_leaderboard: true });
  const [loading, setLoading] = useState(true);
  const [savingGoal, setSavingGoal] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchStreak();
    fetchPrivacy();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await authFetch(`${API}/stats?timeframe=month`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const fetchStreak = async () => {
    try {
      const response = await authFetch(`${API}/streak`);
      if (response.ok) {
        const data = await response.json();
        setStreak(data);
      }
    } catch (error) {
      console.error("Failed to load streak");
    }
  };

  const fetchPrivacy = async () => {
    try {
      const response = await authFetch(`${API}/privacy`);
      if (response.ok) {
        const data = await response.json();
        setPrivacy(data);
      }
    } catch (error) {
      console.error("Failed to load privacy settings");
    }
  };

  const updatePrivacy = async (showOnLeaderboard) => {
    setSavingPrivacy(true);
    try {
      const response = await authFetch(`${API}/privacy`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ show_on_leaderboard: showOnLeaderboard }),
      });
      if (response.ok) {
        setPrivacy({ show_on_leaderboard: showOnLeaderboard });
        toast.success(showOnLeaderboard ? "Stats visible on leaderboard" : "Stats hidden from leaderboard");
      } else {
        toast.error("Failed to update privacy");
      }
    } catch (error) {
      toast.error("Failed to update privacy");
    } finally {
      setSavingPrivacy(false);
    }
  };

  const updateWeeklyGoal = async (newGoal) => {
    setSavingGoal(true);
    try {
      const response = await authFetch(`${API}/streak/goal`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ weekly_goal: parseInt(newGoal) }),
      });
      if (response.ok) {
        const data = await response.json();
        setStreak(prev => ({
          ...prev,
          weekly_goal: data.weekly_goal,
          current_streak: data.current_streak,
          current_week_days: data.current_week_days,
          current_week_goal_met: data.current_week_goal_met,
        }));
        toast.success("Weekly goal updated!");
      } else {
        toast.error("Failed to update goal");
      }
    } catch (error) {
      toast.error("Failed to update goal");
    } finally {
      setSavingGoal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Profile Header */}
          <Card className="bg-card border-border mb-8">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <Avatar className="w-24 h-24">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-3xl">
                    {user?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="text-center md:text-left">
                  <h1 className="font-heading text-3xl font-bold mb-2">{user?.name}</h1>
                  <div className="flex flex-col md:flex-row gap-4 text-muted-foreground">
                    <span className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {user?.email}
                    </span>
                    {user?.created_at && (
                      <span className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Joined {formatDate(user?.created_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Panel */}
          <h2 className="font-heading text-2xl font-bold mb-6">Subscription</h2>
          <div className="mb-8">
            <SubscriptionPanel />
          </div>

          {/* Training Goals */}
          <h2 className="font-heading text-2xl font-bold mb-6">Training Goals</h2>
          <Card className="bg-card border-border mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                {/* Streak Display */}
                <div className="flex items-center gap-4 flex-1">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-full ${
                    streak?.current_streak > 0 ? "bg-orange-500/20" : "bg-muted"
                  }`}>
                    <Flame className={`w-7 h-7 ${
                      streak?.current_streak > 0 ? "text-orange-500" : "text-muted-foreground"
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-3xl font-bold ${
                        streak?.current_streak > 0 ? "text-orange-500" : "text-muted-foreground"
                      }`}>
                        {streak?.current_streak || 0}
                      </span>
                      <span className="text-muted-foreground">week streak</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Longest: {streak?.longest_streak || 0} weeks
                    </p>
                  </div>
                </div>

                {/* Weekly Goal Selector */}
                <div className="flex-1 max-w-xs">
                  <Label className="text-sm text-muted-foreground mb-2 block">
                    Weekly Training Goal
                  </Label>
                  <Select 
                    value={String(streak?.weekly_goal || 3)} 
                    onValueChange={updateWeeklyGoal}
                    disabled={savingGoal}
                  >
                    <SelectTrigger data-testid="weekly-goal-select">
                      <SelectValue placeholder="Select days" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day per week</SelectItem>
                      <SelectItem value="2">2 days per week</SelectItem>
                      <SelectItem value="3">3 days per week</SelectItem>
                      <SelectItem value="4">4 days per week</SelectItem>
                      <SelectItem value="5">5 days per week</SelectItem>
                      <SelectItem value="6">6 days per week</SelectItem>
                      <SelectItem value="7">7 days per week (Daily!)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    Hit your weekly goal to build your streak
                  </p>
                </div>
              </div>

              {/* This Week Status */}
              {streak && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">This Week's Progress</span>
                    <span className={`text-sm font-medium ${
                      streak.current_week_goal_met ? "text-green-500" : "text-muted-foreground"
                    }`}>
                      {streak.current_week_days} / {streak.weekly_goal} days
                      {streak.current_week_goal_met && " ✓"}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Badges */}
          <h2 className="font-heading text-2xl font-bold mb-6">Badges</h2>
          <div className="mb-8">
            <AchievementsCard variant="full" />
          </div>

          {/* Privacy Settings */}
          <h2 className="font-heading text-2xl font-bold mb-6">Privacy</h2>
          <Card className="bg-card border-border mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full ${
                    privacy.show_on_leaderboard ? "bg-primary/20" : "bg-muted"
                  }`}>
                    {privacy.show_on_leaderboard ? (
                      <Eye className="w-6 h-6 text-primary" />
                    ) : (
                      <EyeOff className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">Show on Global Leaderboard</p>
                    <p className="text-sm text-muted-foreground">
                      {privacy.show_on_leaderboard 
                        ? "Your scores are visible to other users" 
                        : "Your scores are hidden from other users"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={privacy.show_on_leaderboard}
                  onCheckedChange={updatePrivacy}
                  disabled={savingPrivacy}
                  data-testid="leaderboard-privacy-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Stats Overview */}
          <h2 className="font-heading text-2xl font-bold mb-6">Your Stats</h2>
          
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i} className="bg-card border-border animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-8 bg-muted rounded mb-2"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <Target className="w-5 h-5 text-muted-foreground mb-2" />
                    <div className="text-3xl font-bold text-primary">{stats.total_sessions}</div>
                    <p className="text-sm text-muted-foreground">Total Sessions</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <Trophy className="w-5 h-5 text-muted-foreground mb-2" />
                    <div className="text-3xl font-bold text-primary">{stats.avg_score}</div>
                    <p className="text-sm text-muted-foreground">Avg Performance</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <Target className="w-5 h-5 text-green-500 mb-2" />
                    <div className="text-3xl font-bold text-green-500">{stats.best_accuracy || 0}</div>
                    <p className="text-sm text-muted-foreground">Best Accuracy</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <Trophy className="w-5 h-5 text-blue-500 mb-2" />
                    <div className="text-3xl font-bold text-blue-500">{stats.best_speed || 0}</div>
                    <p className="text-sm text-muted-foreground">Best Speed</p>
                  </CardContent>
                </Card>
              </div>

              {/* Best Scores by Mode */}
              <h2 className="font-heading text-2xl font-bold mb-6">Personal Bests</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-numbers/10 flex items-center justify-center">
                        <Hash className="w-5 h-5 text-numbers" />
                      </div>
                      <span className="font-semibold">Numbers</span>
                    </div>
                    <div className="text-4xl font-bold text-numbers">{stats.best_numbers_score}</div>
                    <p className="text-sm text-muted-foreground">Best Score</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-secondary/10 flex items-center justify-center">
                        <Type className="w-5 h-5 text-secondary" />
                      </div>
                      <span className="font-semibold">Words</span>
                    </div>
                    <div className="text-4xl font-bold text-secondary">{stats.best_words_score}</div>
                    <p className="text-sm text-muted-foreground">Best Score</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-orange-500/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-orange-500" />
                      </div>
                      <span className="font-semibold">Names</span>
                    </div>
                    <div className="text-4xl font-bold text-orange-500">{stats.best_names_score || 0}</div>
                    <p className="text-sm text-muted-foreground">Best Score</p>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default Profile;
